/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

export class VariableHelper {
    /** All `{{ ... }}` placeholders in order of appearance. */
    private static scanBraced(str: string): { name: string; start: number; end: number }[] {
        const out: { name: string; start: number; end: number }[] = [];
        let i = 0;

        while (i < str.length) {
            const open = str.indexOf('{{', i);
            if (open === -1) break;

            const close = str.indexOf('}}', open + 2);
            if (close === -1) break; // unmatched

            const name = str.slice(open + 2, close).trim();
            out.push({ name, start: open, end: close + 2 });
            i = close + 2;
        }
        return out;
    }

    /** All `${ ... }` expressions inside a template literal. */
    private static scanDollar(str: string): { expr: string; start: number; end: number }[] {
        const out: { expr: string; start: number; end: number }[] = [];
        let i = 0;

        while (i < str.length) {
            const open = str.indexOf('${', i);
            if (open === -1) break;

            // naive but fine for simple identifiers / props
            const close = str.indexOf('}', open + 2);
            if (close === -1) break;

            const expr = str.slice(open + 2, close).trim();
            out.push({ expr, start: open, end: close + 1 });
            i = close + 1;
        }
        return out;
    }

    /**
     * Converts a Heal‑style string (`{{var}}`) to valid TS code
     * (`\`${...}\`` or plain identifier).
     */
    static healVariableToCode(value: string): string {
        const placeholders = VariableHelper.scanBraced(value);

        if (placeholders.length === 0) {
            return VariableHelper.staticHealVariableToCode(value);
        }

        if (placeholders.length === 1 && value.trim() === `{{${placeholders[0].name}}}`) {
            // the whole string is just one placeholder → return identifier
            return placeholders[0].name;
        }

        // build template literal
        let out = '';
        let cursor = 0;
        for (const p of placeholders) {
            out += value.slice(cursor, p.start).replace(/`/g, '\\`');
            out += '${' + p.name + '}';
            cursor = p.end;
        }
        out += value.slice(cursor).replace(/`/g, '\\`');
        return `\`${out}\``;
    }

    /**
     * Inverse of healVariableToCode.
     * Turns code (identifier, template literal, etc.) back into
     * Heal‑style placeholder notation.
     */
    static healCodeToVariable(arg: { value: string; type: string }): string {
        if (arg.type === 'TemplateLiteral') {
            // replace every ${...} with {{...}}
            const parts = VariableHelper.scanDollar(arg.value);
            if (parts.length === 0) return arg.value; // no expressions

            let out = '';
            let cursor = 0;
            for (const p of parts) {
                out += arg.value.slice(cursor, p.start);
                out += '{{' + p.expr + '}}';
                cursor = p.end;
            }
            out += arg.value.slice(cursor);
            return out;
        }

        if (arg.type === 'Identifier' || arg.type === 'CallExpression') {
            return `{{${arg.value}}}`;
        }

        if (arg.type === 'ObjectExpression' || arg.type === 'ArrayExpression') {
            try {
                return JSON.stringify(typeof arg.value === 'string' ? JSON.parse(arg.value) : arg.value);
            } catch {
                // fall through and treat as literal
            }
        }

        if (arg.type === 'Literal') {
            return arg.value;
        }

        return arg.value;
    }

    /**
     * Static (literal) handling
     * @param staticVar
     * @returns
     */
    private static staticHealVariableToCode(staticVar: string): string {
        const isValidIdentifier = (k: string) => /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(k);

        const format = (val: any, lvl = 0): string => {
            const ind = '  '.repeat(lvl);

            if (Array.isArray(val)) {
                if (val.length === 0) return '[]';
                const items = val.map((v) => `${ind}  ${format(v, lvl + 1)}`);
                return `[\n${items.join(',\n')}\n${ind}]`;
            }

            if (typeof val === 'object' && val !== null) {
                const entries = Object.entries(val);
                if (entries.length === 0) return '{}';

                const inner = entries.map(([k, v]) => {
                    const key = isValidIdentifier(k) ? k : `"${k}"`;
                    return `${ind}  ${key}: ${format(v, lvl + 1)}`;
                });
                return `{\n${inner.join(',\n')}\n${ind}}`;
            }

            return JSON.stringify(val);
        };

        let parsed: unknown;
        try {
            parsed = JSON.parse(staticVar);
        } catch {
            return `'${staticVar}'`;
        }

        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return `'${staticVar}'`;
        }

        return format(parsed);
    }
}
